import { createEffect, createSignal, For, onCleanup, Show, useContext } from 'solid-js';
import { Section } from '../../grid-system/section/section';
import { Button } from '../../ui-components/button/button';
import { Heading } from '../../ui-components/heading/heading';
import { Checkbox } from '../../ui-components/inputs/checkbox/checkbox';
import { FlexRow } from '../../ui-components/utility-style-components/flex';
import { CrossIcon } from '../icons-library/cross';
import { Portal } from 'solid-js/web';
import { Category } from './document-holder';
import { getFileSlugTranslations } from './file-translations-map';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { useSearchParams } from '@solidjs/router';
import { getSearchParamAsArray } from './files';

type MobileFilesFilterModalProps = {
    closeModal: () => void;
    applyFiltersText: string;
    topCategoriesHeading: string;
    subCategoriesHeading: string;

    topCategories: Category[];
    subCategories: Category[];

    clickHandlers: {
        handleTopCategoryClick: (category: Category, action: 'add' | 'remove') => void;
        handleSubCategoryClick: (category: Category, action: 'add' | 'remove') => void;
    };
}

export const MobileFilesFilterModal = (props: MobileFilesFilterModalProps) => {
    const { localize } = useContext(AppContext);

    const [searchParams] = useSearchParams();

    const [ inputRef, setInputRef ] = createSignal<HTMLInputElement | undefined>(undefined);

    createEffect(() => {
        if (!inputRef()) return;

        inputRef()!.tabIndex = -1;
        inputRef()!.focus();
        inputRef()!.tabIndex = 0;
    });

    const onKeyPress = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            props.closeModal();
        }
    };

    document.addEventListener('keydown', onKeyPress);

    onCleanup(() => {
        document.removeEventListener('keydown', onKeyPress);
    });

    return (
        <Portal mount={document.getElementById('app')!}>
            <div class="[container-type:inline-size] bg-white h-screen fixed top-[6.43rem] left-0 right-0 z-3 px-6" onScroll={e => e.preventDefault()}>
                <Section
                    templateShorthand={[12]}
                    widthType={'bgFull'}
                    backgroundValue={'white'}
                    backgroundType={'color'}
                    heightType={'fill'}
                    customCss='border-radius: 1.07rem;'
                >
                    <div style={{ 'overflow-y': 'auto', height: '100%'  }}>
                        <FlexRow justifyContent='end' style={{ height: '16px', padding: '1.5rem 0' }}>
                            <CrossIcon size='small' onClick={props.closeModal} ariaLabel={localize('close', 'Close')} />
                        </FlexRow>

                        <div class="h-[calc(100vh-6.43rem-74px-4.5rem)] overflow-auto">

                            <div class="pb-2 border-b border-gray mb-6">
                                <Heading noBlockSpacing={true} tag='h3' variant='small'>
                                    {props.topCategoriesHeading}
                                </Heading>
                            </div>
                            <ul class="mb-8">
                                <For each={props.topCategories}>{(category, index) => (
                                    <li>
                                        <Checkbox
                                            value={category.name}
                                            inputRef={(el: HTMLInputElement) => index() === 0 && setInputRef(el)}
                                            whenClicked={(checkboxEvent) => {
                                                if (checkboxEvent && checkboxEvent.target) {
                                                    const isChecked = checkboxEvent.target.checked;
                                                    if (isChecked) {
                                                        props.clickHandlers.handleTopCategoryClick(category, 'add');
                                                    } else {
                                                        props.clickHandlers.handleTopCategoryClick(category, 'remove');
                                                    }
                                                }
                                            }}
                                            name={getFileSlugTranslations(localize, category.name)}
                                            isChecked={getSearchParamAsArray(searchParams.categories).includes(category.name)}
                                        />
                                    </li>
                                )}</For>
                            </ul>

                            <Show when={props.subCategories?.length > 0}>
                                <div class="pb-2 border-b border-gray mb-6">
                                    <Heading noBlockSpacing={true} tag='h3' variant='small'>
                                        {props.subCategoriesHeading}
                                    </Heading>
                                </div>
                                <ul class="mb-8">
                                    <For each={props.subCategories}>{(category) => (
                                        <li>
                                            <Checkbox
                                                value={category.name}
                                                whenClicked={(checkboxEvent) => {
                                                    if (checkboxEvent && checkboxEvent.target) {
                                                        const isChecked = checkboxEvent.target.checked;
                                                        if (isChecked) {
                                                            props.clickHandlers.handleSubCategoryClick(category, 'add');
                                                        } else {
                                                            props.clickHandlers.handleSubCategoryClick(category, 'remove');
                                                        }
                                                    }
                                                }}
                                                name={getFileSlugTranslations(localize, category.name)}
                                                isChecked={getSearchParamAsArray(searchParams.subcategories).includes(category.name)}
                                            />
                                        </li>
                                    )}</For>
                                </ul>
                            </Show>
                        </div>
                        <FlexRow justifyContent='center' style={{ position: 'fixed', left: '0', right: '0', bottom: '0', height: '74px', background: 'white'}}>
                            <Button label={props.applyFiltersText} onClick={props.closeModal} />
                        </FlexRow>
                    </div>
                </Section>
            </div>
        </Portal>
    );
};